/**
 * Holds the access token in memory, and nowhere else.
 *
 * Module scope rather than React state on purpose: the API client must be able
 * to read the current token without importing a component, and a module-level
 * variable gives exactly one source of truth for "what is the token right now".
 *
 * **No persistence, deliberately.** The fork's admin app stored the refresh token
 * in `localStorage`, which is trap #2 in the audit: any XSS on the panel becomes
 * a permanent credential theft, and unlike the httpOnly cookie a `localStorage`
 * token cannot be revoked by rotating a cookie. So:
 *
 *   • the access token lives only in this variable and dies with the tab;
 *   • the refresh token is an httpOnly cookie the browser manages — this code
 *     never reads it;
 *   • a page reload re-acquires an access token by calling `POST /auth/refresh`
 *     with `credentials: "include"`, which is the "hydration" in
 *     `AuthProvider`.
 *
 * The cost is one extra round trip on boot; the benefit is that the panel holds
 * no credential a script can read.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** The `Authorization` header value, or undefined when there is no token. */
export function authHeader(): Record<string, string> | undefined {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
}